import React, { useState, useEffect } from "react";
import { formatPrice } from "../utils/functions";
import { bidIcon } from "../assets/VehicleDetailsIcon";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";

const SectionHeader = ({ title, icon }) => (
  <h2 className='bg-darkGraybg text-white p-[10px] m-[-15px] mb-[10px] border-b-0 font-bold flex items-center gap-1 text-lg md:text-[18px]'>
    <span>{icon}</span>
    {title}
  </h2>
);

const BidInformation = ({
  vehicle,
  prebidTimeLeft = 0,
  bidAmount = "",
  setBidAmount = () => {},
  bidIncrement = vehicle?.bidIncrement,
  bidMessage = "",
  isSubmittingBid = false,
  handlePreBidSubmit = () => {},
  handleBuyNowSubmit = () => {},
  getMinBidAmount = () => 0,
  className = "",
}) => {
  const { user } = useAuth();
  const { language, translate } = useLanguage();
  const isRTL = language === "ar";

  // Buy now timer state
  const [buyNowTimeLeft, setBuyNowTimeLeft] = useState(() => {
    if (!vehicle?.buyNowEndTime) return 0;
    const endTime = new Date(vehicle.buyNowEndTime);
    const now = new Date();
    return Math.max(0, endTime.getTime() - now.getTime());
  });

  // Track if buy now purchase was successful
  const [isPurchaseSuccessful, setIsPurchaseSuccessful] = useState(false);

  // State for Buy Now confirmation modal
  const [showBuyNowModal, setShowBuyNowModal] = useState(false);

  // Buy now timer effect
  useEffect(() => {
    if (!vehicle?.buyNowEndTime || buyNowTimeLeft <= 0) return;

    const timer = setInterval(() => {
      const endTime = new Date(vehicle.buyNowEndTime);
      const now = new Date();
      const timeLeft = Math.max(0, endTime.getTime() - now.getTime());
      setBuyNowTimeLeft(timeLeft);
    }, 1000);

    return () => clearInterval(timer);
  }, [vehicle?.buyNowEndTime, buyNowTimeLeft]);

  // Effect to detect successful purchase
  useEffect(() => {
    if (
      bidMessage &&
      bidMessage.includes("successfully") &&
      bidMessage.includes("Purchase")
    ) {
      setIsPurchaseSuccessful(true);
    }
  }, [bidMessage]);

  // Handle Buy Now modal close
  const handleBuyNowModalClose = () => {
    setShowBuyNowModal(false);
  };

  // Handle Buy Now button click - show confirmation modal
  const handleBuyNowClick = () => {
    if (!user?.id) {
      alert(translate("bidInfo.alerts.loginToPurchase"));
      return;
    }
    if (user?.seller?.id) {
      alert(translate("bidInfo.alerts.sellerCannotPurchase"));
      return;
    }
    if (!hasActivePackage) {
      alert(translate("bidInfo.alerts.needActivePackagePurchase"));
      return;
    }

    // Show Buy Now confirmation modal
    setShowBuyNowModal(true);
  };

  // Check if vehicle is not yet assigned to any auction lane (future)
  // If auctionLanesVehicles is empty or undefined, it's a future vehicle
  const isFuture =
    !Array.isArray(vehicle?.auctionLanesVehicles) ||
    vehicle?.auctionLanesVehicles?.length === 0;

  // Check if this is a buy now vehicle
  const isBuyNowVehicle =
    vehicle?.ProductStatus?.name === "Buy Now" ||
    vehicle?.ProductStatus === "Buy Now" ||
    (vehicle?.buyNowPrice && vehicle?.buyNowPrice > 0);

  // Check if vehicle is already sold
  const isVehicleSold =
    vehicle?.ProductStatus?.name === "Sold" ||
    vehicle?.ProductStatus === "Sold" ||
    vehicle?.soldPrice ||
    vehicle?.soldTo ||
    vehicle?.ProductAuctionStatus?.name === "Sold" ||
    vehicle?.ProductAuctionStatus === "Sold";

  // Check buyer eligibility based on buyerLimit structure
  const buyerLimit = user?.buyer?.buyerLimit;
  const hasActivePackage =
    buyerLimit &&
    (buyerLimit.remainingCreditLimit > 0 || buyerLimit.remainingCarLimit > 0);
  const remainingCreditLimit = buyerLimit?.remainingCreditLimit || 0;
  const remainingCarLimit = buyerLimit?.remainingCarLimit || 0;
  const currentBidAmount =
    parseFloat(vehicle?.latestBid) || parseFloat(vehicle?.startingBid) || 0;

  const isEligible =
    hasActivePackage &&
    remainingCarLimit > 0 &&
    remainingCreditLimit >= currentBidAmount;

  // Default bid info values
  const bidInfo = {
    eligibilityStatus: isEligible
      ? translate("bidInfo.status.eligible")
      : translate("bidInfo.status.notEligible"),
    startingBid: vehicle?.startingBid || vehicle?.startBidding || 0,
    currentBid: vehicle?.latestBid || 0,
    yourBidStatus: translate("bidInfo.status.notBidYet"),
    marketValue: vehicle?.marketValue || 0,
  };
  const rowClass = isRTL ? "flex-row-reverse text-right" : "";

  return (
    <div
      className={`border p-4 shadow-md bg-white ${className}`}
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >
      <SectionHeader title={translate("bidInfo.title")} icon={bidIcon} />
      <ul className='text-[14px] text-[#242424] font-normal flex flex-col gap-4'>
        {!isBuyNowVehicle && (
          <li className={`flex justify-between ${rowClass}`}>
            <span className='font-semibold'>
              {translate("bidInfo.labels.eligibilityStatus")}:
            </span>
            <div
              className={`flex items-center gap-1 ${
                isRTL ? "flex-row-reverse" : ""
              }`}
            >
              <span className={isEligible ? "text-green-600" : "text-red-600"}>
                {isEligible ? "✓" : "✗"}
              </span>
              <span
                className={
                  isEligible
                    ? "text-green-600 font-semibold"
                    : "text-red-600 font-semibold"
                }
              >
                {bidInfo.eligibilityStatus}
              </span>
            </div>
          </li>
        )}

        {!isBuyNowVehicle && (
          <li className={`flex justify-between ${rowClass}`}>
            <span className='font-semibold'>
              {translate("bidInfo.labels.marketValue")}:
            </span>
            <span className={isRTL ? "text-left" : ""}>
              {formatPrice(vehicle?.marketValue)}
            </span>
          </li>
        )}

        {isBuyNowVehicle ? (
          <>
            <li className={`flex justify-between ${rowClass}`}>
              <span className='font-semibold'>
                {translate("bidInfo.labels.buyNowPrice")}:
              </span>
              <span className='text-green-600 font-bold text-lg'>
                {formatPrice(vehicle?.buyNowPrice)}
              </span>
            </li>
            {vehicle?.buyNowEndTime && (
              <li className={`flex justify-between ${rowClass}`}>
                <span className='font-semibold'>
                  {translate("bidInfo.labels.buyNowTime")}:
                </span>
                <span className='text-red-600 font-semibold text-left'>
                  {buyNowTimeLeft > 0
                    ? (() => {
                        const days = String(
                          Math.floor(buyNowTimeLeft / (1000 * 60 * 60 * 24))
                        ).padStart(2, "0");
                        const hours = String(
                          Math.floor(
                            (buyNowTimeLeft % (1000 * 60 * 60 * 24)) /
                              (1000 * 60 * 60)
                          )
                        ).padStart(2, "0");
                        const minutes = String(
                          Math.floor(
                            (buyNowTimeLeft % (1000 * 60 * 60)) / (1000 * 60)
                          )
                        ).padStart(2, "0");
                        const seconds = String(
                          Math.floor((buyNowTimeLeft % (1000 * 60)) / 1000)
                        ).padStart(2, "0");
                        return `${days} : ${hours} : ${minutes} : ${seconds}`;
                      })()
                    : translate("bidInfo.status.expired")}
                </span>
              </li>
            )}
          </>
        ) : (
          <>
            <li className={`flex justify-between ${rowClass}`}>
              <span className='font-semibold'>
                {translate("bidInfo.labels.startingBid")}:
              </span>
              <span>
                {formatPrice(vehicle?.startingBid || vehicle?.startBidding)}
              </span>
            </li>
            <li className={`flex justify-between ${rowClass}`}>
              <span className='font-semibold'>
                {translate("bidInfo.labels.currentBid")}:
              </span>
              <span id='current-bid' className={isRTL ? "text-left" : ""}>
                {formatPrice(vehicle?.latestBid) || "-"}
              </span>
            </li>
          </>
        )}
        {/* Hide Your Bid Status for Buy Now vehicles and sold vehicles */}
        {!isBuyNowVehicle &&
          !(
            vehicle?.ProductStatus && vehicle?.ProductStatus.name === "Sold"
          ) && (
            <li className={`flex justify-between ${rowClass}`}>
              <span className='font-semibold'>
                {translate("bidInfo.labels.yourBidStatus")}:
              </span>
              <span
                className={
                  !user?.id
                    ? "text-orange-600 font-semibold"
                    : user?.userType === "seller"
                    ? "text-orange-600 font-semibold"
                    : user?.userType === "buyer" &&
                      vehicle?.latestBid &&
                      vehicle?.highestBidderId === user?.id
                    ? "text-green-600 font-semibold"
                    : user?.userType === "buyer" &&
                      vehicle?.latestBid &&
                      user?.id
                    ? "text-red-600 font-semibold"
                    : user?.userType === "buyer"
                    ? "text-orange-600 font-semibold"
                    : "text-orange-600 font-semibold"
                }
              >
                {!user?.id
                  ? translate("bidInfo.status.loginAsBuyer")
                  : user?.userType === "seller"
                  ? translate("bidInfo.status.loginAsBuyer")
                  : user?.userType === "buyer" &&
                    vehicle?.latestBid &&
                    vehicle?.highestBidderId === user?.id
                  ? translate("bidInfo.status.winningBid")
                  : user?.userType === "buyer" && vehicle?.latestBid && user?.id
                  ? translate("bidInfo.status.losingBid")
                  : user?.userType === "buyer"
                  ? translate("bidInfo.status.notBidYet")
                  : translate("bidInfo.status.loginAsBuyer")}
              </span>
            </li>
          )}
        {/* Prebid Timer - only show if there's actually a timer and it's not a buy now vehicle */}
        {!isBuyNowVehicle &&
          vehicle?.auctionLanesVehicles?.[0]?.auctionLane?.auction &&
          prebidTimeLeft > 0 && (
            <li className={`flex justify-between ${rowClass}`}>
              <span className='font-semibold text-primary animate-blink'>
                {translate("bidInfo.labels.prebidTimeLeft")}:
              </span>
              <span className={isRTL ? "text-left" : ""}>
                {(() => {
                  if (
                    !vehicle?.auctionLanesVehicles?.[0]?.auctionLane?.auction
                      ?.preBidEndDate
                  )
                    return "-";
                  const days = String(
                    Math.floor(prebidTimeLeft / (1000 * 60 * 60 * 24))
                  ).padStart(2, "0");
                  const hours = String(
                    Math.floor(
                      (prebidTimeLeft % (1000 * 60 * 60 * 24)) /
                        (1000 * 60 * 60)
                    )
                  ).padStart(2, "0");
                  const minutes = String(
                    Math.floor(
                      (prebidTimeLeft % (1000 * 60 * 60)) / (1000 * 60)
                    )
                  ).padStart(2, "0");
                  const seconds = String(
                    Math.floor((prebidTimeLeft % (1000 * 60)) / 1000)
                  ).padStart(2, "0");
                  return `${days} : ${hours} : ${minutes} : ${seconds}`;
                })()}
              </span>
            </li>
          )}

        {/* Buy Now or Bid Input Section */}
        {isBuyNowVehicle ? (
          /* Buy Now Section */
          <li className='mt-2'>
            <div className='text-center'>
              {isFuture ||
              buyNowTimeLeft <= 0 ||
              isPurchaseSuccessful ||
              isVehicleSold ? (
                <div className='w-full text-[14px] font-medium text-white py-2 bg-gray-400 cursor-not-allowed text-center'>
                  {isPurchaseSuccessful
                    ? translate("bidInfo.status.purchaseSuccessful")
                    : isVehicleSold
                    ? translate("bidInfo.status.vehicleSold")
                    : buyNowTimeLeft <= 0
                    ? translate("bidInfo.status.buyNowExpired")
                    : translate("bidInfo.status.future")}
                </div>
              ) : (
                <button
                  className='w-full text-[16px] font-bold text-white py-3 bg-green-600 hover:bg-green-700 transition-colors duration-200'
                  onClick={handleBuyNowClick}
                  disabled={isSubmittingBid}
                >
                  {isSubmittingBid
                    ? translate("bidInfo.buttons.processing")
                    : translate("bidInfo.buttons.buyNow")}
                </button>
              )}
            </div>
          </li>
        ) : (
          /* Prebid Section */
          handlePreBidSubmit && (
            <li className='mt-2'>
              {isVehicleSold ? (
                /* Vehicle Sold Button */
                <div className='w-full text-[14px] font-medium text-white py-2 bg-red-600 cursor-not-allowed text-center'>
                  {translate("bidInfo.status.vehicleSold")}
                </div>
              ) : (
                <>
                  <div
                    className={`flex items-end justify-between ${
                      isRTL ? "flex-row-reverse" : ""
                    }`}
                  >
                    <span className='font-semibold'>
                      {translate("bidInfo.labels.makePreBid")}:
                    </span>
                    <div
                      className={`flex items-center gap-1 ${
                        isRTL ? "flex-row-reverse" : ""
                      }`}
                    >
                      <button
                        className={`px-2 py-1 ${
                          isFuture
                            ? "bg-gray-300 cursor-not-allowed"
                            : "bg-[#DFDFDF] hover:bg-gray-200"
                        }`}
                        onClick={
                          isFuture
                            ? undefined
                            : () => {
                                // For decrement, we'll still use the manual calculation
                                // since updateBidAmount always increases the bid
                                const currentAmount =
                                  parseFloat(bidAmount) || 0;
                                // Get minimum bid amount
                                const startingBid =
                                  parseFloat(vehicle?.startingBid) || 0;
                                const currentBid =
                                  parseFloat(vehicle?.latestBid) || 0;
                                const minBidAmount =
                                  currentBid > 0
                                    ? currentBid + bidIncrement
                                    : startingBid + bidIncrement;

                                // Calculate new amount but don't go below minimum
                                const newAmount = Math.max(
                                  minBidAmount,
                                  currentAmount - bidIncrement
                                );
                                setBidAmount(newAmount.toString());
                              }
                        }
                        disabled={isFuture}
                      >
                        -
                      </button>
                      <input
                        type='number'
                        value={bidAmount}
                        onChange={
                          isFuture
                            ? undefined
                            : (e) => {
                                const newValue = e.target.value;
                                // Calculate minimum bid amount
                                const startingBid =
                                  parseFloat(vehicle?.startingBid) || 0;
                                const currentBid =
                                  parseFloat(vehicle?.latestBid) || 0;
                                const minBidAmount =
                                  currentBid > 0
                                    ? currentBid + bidIncrement
                                    : startingBid + bidIncrement;

                                // Validate that the new value is not below minimum
                                if (
                                  newValue === "" ||
                                  parseFloat(newValue) >= minBidAmount
                                ) {
                                  setBidAmount(newValue);
                                }
                              }
                        }
                        className={`px-4 w-24 py-1 font-medium text-[14px] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                          isFuture
                            ? "bg-gray-300 cursor-not-allowed"
                            : "bg-[#DFDFDF]"
                        } ${isRTL ? "text-right" : ""}`}
                        placeholder={translate(
                          "bidInfo.labels.amountPlaceholder"
                        )}
                        disabled={isFuture}
                      />
                      <button
                        className={`px-2 py-1 ${
                          isFuture
                            ? "bg-gray-300 cursor-not-allowed"
                            : "bg-[#DFDFDF] hover:bg-gray-200"
                        }`}
                        onClick={
                          isFuture
                            ? undefined
                            : () => {
                                const currentAmount =
                                  parseFloat(bidAmount) || 0;
                                // Calculate minimum bid amount
                                const startingBid =
                                  parseFloat(vehicle?.startingBid) || 0;
                                const currentBid =
                                  parseFloat(vehicle?.latestBid) || 0;
                                const minBidAmount =
                                  currentBid > 0
                                    ? currentBid + bidIncrement
                                    : startingBid + bidIncrement;

                                // Calculate new amount - ensure it's at least minimum
                                const newAmount = Math.max(
                                  minBidAmount,
                                  currentAmount + bidIncrement
                                );
                                setBidAmount(newAmount.toString());
                              }
                        }
                        disabled={isFuture}
                      >
                        +
                      </button>
                    </div>
                  </div>

                  {isFuture ? (
                    <div className='mt-3 w-full text-[14px] font-medium text-white py-2 bg-gray-400 cursor-not-allowed text-center'>
                      {translate("bidInfo.status.future")}
                    </div>
                  ) : (
                    <button
                      className='mt-3 w-full text-[14px] font-medium text-white py-2 bg-primary-dark hover:bg-primary-dark/90'
                      onClick={() => {
                        if (!user?.id) {
                          alert(translate("bidInfo.alerts.loginToBid"));
                          return;
                        }
                        if (user?.seller?.id) {
                          alert(translate("bidInfo.alerts.sellerCannotBid"));
                          return;
                        }
                        if (!hasActivePackage) {
                          alert(
                            translate("bidInfo.alerts.needActivePackageBid")
                          );
                          return;
                        }
                        handlePreBidSubmit();
                      }}
                      disabled={isSubmittingBid}
                    >
                      {isSubmittingBid
                        ? translate("bidInfo.buttons.submitting")
                        : translate("bidInfo.buttons.prebid")}
                    </button>
                  )}

                  {/* Bid Message Display */}
                  {bidMessage && (
                    <div
                      className={`mt-2 p-2 rounded text-sm ${
                        bidMessage.includes("Error")
                          ? "bg-red-100 text-red-700 border border-red-200"
                          : bidMessage.includes("successfully")
                          ? "bg-green-100 text-green-700 border border-green-200"
                          : "bg-blue-100 text-blue-700 border border-blue-200"
                      }`}
                    >
                      {bidMessage}
                    </div>
                  )}
                </>
              )}
            </li>
          )
        )}
      </ul>

      {/* Buy Now Confirmation Modal */}
      {showBuyNowModal && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div
            className='bg-white rounded-lg p-6 max-w-md w-full mx-4'
            dir={isRTL ? "rtl" : "ltr"}
            lang={language}
          >
            {/* Header */}
            <div
              className={`flex justify-between items-center mb-4 ${
                isRTL ? "flex-row-reverse" : ""
              }`}
            >
              <h3 className='text-lg font-bold text-gray-900'>
                {translate("bidInfo.labels.buyNowConfirmation")}
              </h3>
              <button
                onClick={handleBuyNowModalClose}
                className='text-gray-400 hover:text-gray-600'
              >
                <svg
                  className='w-6 h-6'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </button>
            </div>

            {/* Vehicle Information */}
            <div className='mb-6'>
              <div
                className={`flex items-center mb-4 ${
                  isRTL
                    ? "space-x-reverse space-x-4 flex-row-reverse"
                    : "space-x-4"
                }`}
              >
                <img
                  src={vehicle?.images?.[0]?.imageUrl || "/car-placeholder.png"}
                  alt={vehicle?.name}
                  className='w-16 h-12 object-cover rounded'
                />
                <div>
                  <h4 className='font-semibold text-gray-900'>
                    {vehicle?.name}
                  </h4>
                  <p className='text-sm text-gray-600'>
                    Lot: {vehicle?.lotNumber}
                  </p>
                </div>
              </div>

              <div className='bg-gray-50 p-4 rounded-lg'>
                <div
                  className={`flex justify-between items-center mb-2 ${
                    isRTL ? "flex-row-reverse" : ""
                  }`}
                >
                  <span className='text-gray-600'>
                    {translate("bidInfo.labels.buyNowPrice")}:
                  </span>
                  <span className='text-xl font-bold text-green-600'>
                    {formatPrice(vehicle?.buyNowPrice)}
                  </span>
                </div>
                {vehicle?.buyNowEndTime && (
                  <div
                    className={`flex justify-between items-center ${
                      isRTL ? "flex-row-reverse" : ""
                    }`}
                  >
                    <span className='text-gray-600'>
                      {translate("bidInfo.labels.buyNowAvailableUntil")}:
                    </span>
                    <span className='text-sm text-gray-800'>
                      {new Date(vehicle.buyNowEndTime).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Confirmation Message */}
            <div className='mb-6'>
              <p className='text-gray-700 text-center'>
                {translate("bidInfo.labels.buyNowMessagePrefix")}{" "}
                <span className='font-bold text-green-600'>
                  {formatPrice(vehicle?.buyNowPrice)}
                </span>
                {translate("bidInfo.labels.buyNowMessageSuffix")}
              </p>
            </div>

            {/* Action Buttons */}
            <div
              className={`flex ${
                isRTL ? "space-x-reverse space-x-3" : "space-x-3"
              }`}
            >
              <button
                onClick={handleBuyNowModalClose}
                className='flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200'
              >
                {translate("bidInfo.buttons.cancel")}
              </button>
              <button
                onClick={() => {
                  setShowBuyNowModal(false);
                  if (handleBuyNowSubmit) {
                    handleBuyNowSubmit();
                  }
                }}
                className='flex-1 px-4 py-2 bg-gradient-to-r from-[#FF4A33] to-[#FFB74D] text-white rounded-lg hover:shadow-lg transition-all duration-200 font-semibold'
              >
                {translate("bidInfo.buttons.confirmPurchase")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BidInformation;
